import { Component, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

interface NotificationSetting {
    value?: boolean;
    label: Observable<string>;
    ariaLabel: Observable<string>;
}

@Component({
    selector: 'sfx-notification-settings',
    templateUrl: './notification-settings.component.html',
    styleUrls: ['./notification-settings.component.scss']
})
export class NotificationSettingsComponent implements OnInit {

    public loading = false;

    notifications: Map<string, NotificationSetting> = new Map<string, NotificationSetting>();

    constructor(
        @Inject('notifications') private notificationService: any,
        private translate: TranslateService
    ) {
        // Initialize notifications with translated labels
        this.notifications.set('email', {
            label: this.translate.get('Email'),
            ariaLabel: this.translate.get('Email')
        });
        this.notifications.set('email_open', {
            label: this.translate.get('Email open'),
            ariaLabel: this.translate.get('Email opens')
        });
        this.notifications.set('forward', {
            label: this.translate.get('Email click-through'),
            ariaLabel: this.translate.get('Email click-throughs')
        });
        this.notifications.set('meeting-live', {
            label: this.translate.get('Meeting by teammate'),
            ariaLabel: this.translate.get('Meetings')
        });
        this.notifications.set('prep_meeting', {
            label: this.translate.get('Upcoming meeting'),
            ariaLabel: this.translate.get('Upcoming meeting')
        });
        this.notifications.set('minutes_meeting', {
            label: this.translate.get('Meeting notes'),
            ariaLabel: this.translate.get('Meeting notes')
        });
        this.notifications.set('meeting-phone', {
            label: this.translate.get('Phone call by teammate'),
            ariaLabel: this.translate.get('Phone calls')
        });
        this.notifications.set('assignee', {
            label: this.translate.get('Someone assigned you to an opportunity'),
            ariaLabel: this.translate.get('Someone assigned you to an opportunity')
        });
        this.notifications.set('stage_change', {
            label: this.translate.get('Opportunity stage change'),
            ariaLabel: this.translate.get('Opportunity stage change')
        });
        this.notifications.set('webpage', {
            label: this.translate.get('Webpage visit'),
            ariaLabel: this.translate.get('Webpage visit')
        });
        this.notifications.set('message', {
            label: this.translate.get('Every internal note'),
            ariaLabel: this.translate.get('All internal notes')
        });
        this.notifications.set('mention', {
            label: this.translate.get('Only internal notes that mention me'),
            ariaLabel: this.translate.get('Internal notes with mentions')
        });
        this.notifications.set('lead_hotness', {
            label: this.translate.get('Lead/account hotness'),
            ariaLabel: this.translate.get('Lead hotness')
        });
        this.notifications.set('user_added', {
            label: this.translate.get('Someone is added to an account'),
            ariaLabel: this.translate.get('Someone is added to an account')
        });
        this.notifications.set('user_removed', {
            label: this.translate.get('Someone is removed from an account'),
            ariaLabel: this.translate.get('Someone is removed from an account')
        });
        this.notifications.set('task', {
            label: this.translate.get('Task due'),
            ariaLabel: this.translate.get('Task due')
        });
        this.notifications.set('task_inactivity', {
            label: this.translate.get('Suggested task when an account becomes inactive'),
            ariaLabel: this.translate.get('Suggested task when an account becomes inactive')
        });
        this.notifications.set('task_assigned', {
            label: this.translate.get('Someone assigned a task to you'),
            ariaLabel: this.translate.get('Someone assigned a task to you')
        });
        this.notifications.set('setup_step_completed', {
            label: this.translate.get('A step in the setup is completed'),
            ariaLabel: this.translate.get('A step in the setup is completed')
        });
    }

    public ngOnInit(): void {

        this._loadSettings();
    }

    private _loadSettings(): void {

        this.loading = true;

        this.notificationService.getSettings().then((response: any) => {

            for (const [key, value] of Object.entries<boolean>(response.data)) {

                this._setValue(key, value);
            }

            this.loading = false;
        });
    }

    private _getValue(key: string): boolean {

        return this.notifications.get(key)?.value || false;
    }

    private _setValue(key: string, value: boolean): void {

        if (this.notifications.has(key)) {

            const setting = this.notifications.get(key) as NotificationSetting;

            setting.value = value;

            this.notifications.set(key, setting);
        }
    }

    public updateSettings(updatedSettingKey: string): void {

        const apiSettings: { [key: string]: boolean } = {};

        switch (updatedSettingKey) {
            case 'mention':
                this._setValue('message', !this._getValue('mention'));
                break;
            case 'message':
                this._setValue('mention', !this._getValue('message'));
                break;
        }

        for (const [key, value] of this.notifications) {

            apiSettings[key] = value.value || false;
        }

        return this.notificationService.updateSettings(apiSettings)
            .then(() => {

                this._loadSettings();
            });
    }

    public keepOrder() {

        return 0;
    }
}
